import type { components } from "@sagot/api-client";
import { consoleRoutes, type CategorySource } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Meter, type BadgeTone } from "@sagot/ui";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { statusLabelOf } from "../../service/organisations/ticket-parts";
import { useAccuracy } from "../../service/tickets/views/needs-category-api";

type Listed = Pick<components["schemas"]["TicketSummary"], "type" | "predicted_type_id" | "predicted_score" | "category_source">;

/** What the chip says about a category: applied by itself, waiting for a person, or set by someone. */
export type CategoryState = "applied" | "review" | CategorySource;

/** `null` for a ticket the categorizer never handled (an older ticket, or one a person raised): nothing to show. */
export function categoryStateOf(ticket: Pick<Listed, "predicted_type_id" | "category_source">): CategoryState | null {
  if (ticket.category_source === "auto") return "applied";
  if (ticket.category_source === "agent" || ticket.category_source === "customer") return ticket.category_source;
  return ticket.predicted_type_id ? "review" : null;
}

const TONE: Readonly<Record<CategoryState, BadgeTone>> = { applied: "brand", review: "warning", auto: "brand", agent: "neutral", customer: "neutral" };

/** "0.91 · applied" or "needs review": the category's score and who settled it (the mockup's `tag blue`). */
export function CategoryChip({ ticket, compact = false }: { ticket: Listed; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const state = categoryStateOf(ticket);
  if (state === null) return null;
  const name = statusLabelOf(ticket.type.name, i18n.language) ?? ticket.type.system_key ?? "";
  const score = ticket.predicted_score;
  const label = state === "review" ? t("service.categorization.chip.review") : state === "applied" ? t("service.categorization.chip.applied") : t(`service.categorization.chip.${state}`);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs" data-testid="category-chip" data-state={state}>
      {compact ? null : <span className="font-semibold text-fg">{name}</span>}
      <Badge tone={TONE[state]}>{score !== null && score !== undefined && (state === "applied" || state === "review") ? `${score.toFixed(2)} · ${label}` : label}</Badge>
    </span>
  );
}

/** The "Category" block of the details pane (inbox pin 5): the category, its score against the workspace threshold, and where to change it. */
export function CategorySection({ ticket }: { ticket: Listed }) {
  const { t, i18n } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const canReport = permissions.can("tickets.reports", { anywhere: true });
  const state = categoryStateOf(ticket);
  const accuracy = useAccuracy(canReport && state !== null);
  if (state === null) return null;
  const score = ticket.predicted_score;
  const threshold = accuracy.data?.threshold ?? null;
  const labelled = accuracy.data?.labelled_tickets;
  const name = statusLabelOf(ticket.type.name, i18n.language) ?? ticket.type.system_key ?? "";
  return (
    <div className="mt-2 space-y-1" data-testid="category-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-fg">{name}</span>
        <CategoryChip ticket={ticket} />
      </div>
      {score !== null && score !== undefined ? (
        <Meter value={Math.round(score * 100)} max={100} label={t("service.categorization.chip.meter", { name })} valueLabel={score.toFixed(2)} threshold={threshold === null ? undefined : Math.round(threshold * 100)} thresholdLabel={threshold === null ? undefined : t("service.categorization.chip.thresholdMark", { threshold: threshold.toFixed(2) })} />
      ) : null}
      {canReport && threshold !== null && labelled !== undefined ? (
        <p className="text-xs text-fg-muted">
          {t("service.categorization.chip.threshold", { threshold: threshold.toFixed(2), count: labelled })}{" "}
          <AppLink href={consoleRoutes.serviceReports(summary.slug, "accuracy")} className="font-semibold text-primary-ink hover:underline focus-visible:outline-2 focus-visible:outline-primary">
            {t("service.categorization.chip.change")}
          </AppLink>
        </p>
      ) : null}
    </div>
  );
}
